import { Component, ViewEncapsulation, signal, inject, output, input, OnDestroy } from '@angular/core';
import { CoreService } from 'src/app/services/core.service';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { RouterModule, TitleStrategy } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { AppSettings, DEVELOPER_PORTAL_SYSTEM_ID } from 'src/app/config';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { NotificationViewModel, RenewMessageViewModel, SettingsAPIClient } from 'src/app/services/settings.api.client';
import { map, Subscription, tap } from 'rxjs';
import { FinancialYearViewModel, FinAPIClient } from 'src/app/services/fin.api.client';
import { FinancialYearService } from 'src/app/services/financial.year.service';
import { UserAuthService } from 'src/app/services/user-auth.service';
import { WikiHelpService } from 'src/app/services/wiki-help.service';
import { KbChatService } from 'src/app/services/kb-chat.service';
import { ZohoImService } from 'src/app/services/zoho-im.service';
import { appLanguages } from 'projects/crm/src/app/app.component';
import {
  ACCOUNT_SETUP_URL,
  AccountSetupProgress,
  loadAccountSetupProgress,
  readAccountSetupProgress,
} from 'src/app/onboarding/account-setup-progress';
import { App } from '../../full.component';
interface profiledd {
  id: number;
  img: string;
  title: string;
  subtitle: string;
  link: string;
}

@Component({
  selector: 'app-header',
  imports: [RouterModule, CommonModule, NgScrollbarModule, TablerIconsModule, MaterialModule, TranslateModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent implements OnDestroy {
  finApiClient = inject(FinAPIClient);
  userAuthService = inject(UserAuthService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  settingsAPIClient = inject(SettingsAPIClient);
  financialYearService = inject(FinancialYearService);
  wikiHelpService = inject(WikiHelpService);
  kbChatService = inject(KbChatService);
  private readonly zohoImService = inject(ZohoImService);
  userData = this.oidcSecurityService.userData;

  optionsChange = output<AppSettings>();
  showToggle = input(true);
  toggleChecked = input(false);
  toggleMobileNav = output<void>();
  toggleMobileFilterNav = output<void>();
  toggleAskPanel = output<void>();
  onAppsLoaded = output<App[]>();
  toggleCollapsed = output<void>();
  notifications = signal<NotificationViewModel[]>([]);
  renewalMessage = signal<RenewMessageViewModel | null>({});
  homePage = input(false);

  isLoading = signal(false);

  isLoadingCompanyRenewal = signal(false);

  apps = signal<App[]>([]);
  showFiller = false;

  private appsSubscription?: Subscription;

  languages = appLanguages;
  public selectedLanguage: any = window.localStorage.getItem('language')
    ? JSON.parse(window.localStorage.getItem('language')!)
    : this.languages[0];

  showFinancialYearsDropdown = signal(false);

  // Help & support panel. Shown for every company in an app that wires Zoho's support widget
  // (its "Technical support" tab is not tied to the Ask AI rollout); otherwise only once the
  // company is in the Ask AI rollout — see applyRolloutDecision().
  showAskButton = signal(this.zohoImService.isWired);

  visitingCompanyName = signal<string | null>(null);

  /** Account setup (the onboarding wizard): offered whenever it is switched on; the badge counts steps not completed. */
  accountSetup = signal<AccountSetupProgress | null>(readAccountSetupProgress());
  readonly accountSetupUrl = ACCOUNT_SETUP_URL;

  // Open in same tab while visiting a company.
  get systemReportsTarget(): string {
    return sessionStorage.getItem('v_id')?.trim() ? '_self' : '_blank';
  }

  constructor(
    private settings: CoreService,
    public dialog: MatDialog,
    private translate: TranslateService,
  ) {
    translate.setDefaultLang(this.selectedLanguage.code);
    this.settings.setLanguage(this.selectedLanguage);

    // if (this.selectedFinancialYear) {
    //   this.financialYearService.setFinancialYear(this.selectedFinancialYear);
    // }

    this.loadNotifications();
    this.loadAccountSetup();

    this.appsSubscription = this.settingsAPIClient
      .getUserSystemListWithCompanyData(this.selectedLanguage.languageDir === 'rtl')
      .pipe(
        // Ask AI rollout: decided on the resolved CompanyID, not just at construction time. The
        // decision gates the panel's "Related articles" and "Ask AI" tabs (see
        // `KbChatService.companyInRollout`, read by `AskPanelComponent`), and the button itself in
        // an app without Zoho's support widget. A company outside the rollout sees only the
        // "Technical support" tab.
        //
        // The rollout decision is backend-authoritative — the company's subscription plan (Grow),
        // evaluated server-side. There is no client-side fallback criterion: if `getRolloutStatus()`
        // is unreachable, we fail closed (gate stays disabled) rather than guess. Non-blocking: the
        // tap fires and moves on immediately, the button appears once the decision lands.
        tap(() => {
          this.kbChatService
            .getRolloutStatus()
            .then((enabled) => this.applyRolloutDecision(enabled))
            .catch(() => this.applyRolloutDecision(false));
        }),
        map((val) => {
          return (
            val.AllSystemsList?.filter((e) => e.IsAuthorized || e.SystemID !== DEVELOPER_PORTAL_SYSTEM_ID).map((e) => {
              return {
                id: e.SystemID ?? 0,
                img: e.LogoPath ?? '',
                title: e.SystemTitle ?? '',
                subtitle:
                  this.selectedLanguage.code === 'ar' ? (e.SystemDescription ?? '') : (e.SystemDescriptionEn ?? ''),
                link: '/' + e.Url,
              };
            }) ?? []
          );
        }),
      )
      .subscribe({
        next: (val) => {
          this.apps.set(val);
          // Check if component is still alive before emitting
          if (this.onAppsLoaded) {
            try {
              this.onAppsLoaded.emit(val);
            } catch (error) {
              // Silently catch errors if component is destroyed
            }
          }
        },
      });

    this.loadRenewalMessage();

    if (this.userAuthService.systemId() == 28) {
      this.showFinancialYearsDropdown.set(true);
      this.loadFinancialYears();
    }

    // In an app without Zoho's support widget, showAskButton is set once CompanyID resolves and the
    // backend rollout decision lands — see the `tap()` above and `applyRolloutDecision()`. It then
    // requires both wikiHelpService.isConfigured (the app wires the wiki base URL) and the company
    // being in the rollout.

    //get visiting company name
    if (sessionStorage.getItem('v_id') && this.userAuthService.systemId() != 35) {
      this.visitingCompanyName.set(sessionStorage.getItem('v_n'));
    }
  }

  /**
   * Apply the backend `RolloutStatus` decision (or `false` when that call fails — fail closed)
   * to both the chat-panel gate and the header button.
   */
  private applyRolloutDecision(inRollout: boolean): void {
    this.kbChatService.companyInRollout.set(inRollout);

    if (this.wikiHelpService.isConfigured && inRollout) {
      this.showAskButton.set(true);
    }
  }

  ngOnDestroy(): void {
    // Clean up subscription to prevent emitting after component destruction
    if (this.appsSubscription) {
      this.appsSubscription.unsubscribe();
    }
  }

  logout() {
    // Forget Zoho's support chat too, so the next person on a shared shop computer doesn't continue it.
    this.zohoImService.logout();
    this.oidcSecurityService.logoff().subscribe((result: any) => {
      console.log('logout: ', result);
      sessionStorage.clear();
      this.financialYearService.clearSelectedFinancialYear();
    });
  }

  options = this.settings.getOptions();
  SetTheme(theme: string) {
    if (theme === 'dark') {
      this.options.theme = 'light';
    } else {
      this.options.theme = 'dark';
    }
    this.optionsChange.emit(this.options);
  }

  updateAppSettings() {
    this.optionsChange.emit(this.options);
  }

  changeLanguage(lang: any): void {
    if (lang.code != this.selectedLanguage.code) {
      this.translate.use(lang.code);
      this.selectedLanguage = lang;

      this.settings.setLanguage(this.selectedLanguage);

      this.updateAppSettings();
      location.reload();
    }
  }

  /**
   * The Account setup icon, so the merchant can come back to the wizard: to the steps still to do or skipped, or to
   * review a finished setup. Shown when the user's authorized Settings pages include Account setup; read once per
   * session and shared by every app (loadAccountSetupProgress).
   */
  private loadAccountSetup(): void {
    if (this.accountSetup()) return;
    // A failed read leaves no icon this time; the next page load asks again.
    loadAccountSetupProgress(this.settingsAPIClient, this.selectedLanguage.code).subscribe((progress) => {
      if (progress) this.accountSetup.set(progress);
    });
  }

  loadNotifications() {
    this.isLoading.set(true);

    this.settingsAPIClient
      .getNotifications()
      .pipe(map((val) => val.slice(0, 3)))
      .subscribe({
        next: (value) => {
          this.isLoading.set(false);

          this.notifications.set(value);
        },
        error: (err) => {
          this.isLoading.set(false);
          console.error(err);
        },
      });
  }

  loadRenewalMessage() {
    this.isLoadingCompanyRenewal.set(true);

    this.settingsAPIClient.getRenewMessage().subscribe({
      next: (value) => {
        this.isLoadingCompanyRenewal.set(false);

        this.renewalMessage.set(value);
      },
      error: (err) => {
        this.isLoadingCompanyRenewal.set(false);
        console.error(err);
      },
    });
  }

  changeFinancialYear(year: FinancialYearViewModel): void {
    if (year.FinancailYearID != this.financialYearService.FinancialYear()?.FinancailYearID) {
      this.financialYearService.setFinancialYear(year);
    }
  }

  loadFinancialYears() {
    //this.isLoading.set(true);

    this.finApiClient.getFinancialYears().subscribe({
      next: (value) => {
        //this.isLoading.set(false);
        this.financialYearService.setFinancialYearsList(value);
        //console.log(value);
      },
      error: (err) => {
        //this.isLoading.set(false);
        console.error(err);
      },
    });
  }

  navigateToHome() {
    const settingsApp = this.apps().find((app) => app.id === 34);

    if (settingsApp) {
      // Add query param to force home page
      window.location.href = settingsApp.link + '?Home=true';
    }
  }

  profiledd: profiledd[] = [
    {
      id: 1,
      img: 'assets/images/svgs/icon-account.svg',
      title: 'My Profile',
      subtitle: 'Account Settings',
      link: '/settings/MyAccount',
    },
  ];
}
